import React, { useState, useEffect } from 'react';
import EvidenciaPraceForm from './components/EvidenciaPraceForm';
import { Link } from 'react-router-dom';
import './EvidenciaPrace.css';
import { calculateEntryTotal, calculateTotal, getFilteredEntries, normalizeText, capitalizeCompanyName } from './utils/evidenciaPraceUtils';
import { useEvidenciaPrace } from './context/EvidenciaPraceContext';

// Pridáme jednoduchý štýl pre sidebar inline, ak nie je v CSS

const EvidenciaPrace = () => {
  const {
    workEntries, setWorkEntries,
    companies, setCompanies,
    selectedCompany, setSelectedCompany,
    filterMonth, setFilterMonth,
    showInvoiced, setShowInvoiced,
    selectedForInvoice, setSelectedForInvoice,
    pendingEntry, setPendingEntry
  } = useEvidenciaPrace();

  const [pin, setPin] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Formulár pre nový záznam
  const [newEntry, setNewEntry] = useState({
    companyName: '',
    date: new Date().toISOString().split('T')[0],
    workPeriodFrom: new Date().toISOString().slice(0, 7),
    workPeriodTo: '',
    description: '',
    minutes: 0,
    unitPrice: 0,
    actualInvoicePrice: 0,
    note: ''
  });

  // State pre inline editovanie
  const [editingId, setEditingId] = useState(null);
  const [editedEntry, setEditedEntry] = useState(null);

  // Načítaj pendingEntry z kontextu (pre-fill z Povinnosti)
  useEffect(() => {
    if (pendingEntry) {
      setNewEntry(prev => ({ ...prev, ...pendingEntry }));
      setIsFormOpen(true);
      setPendingEntry(null);
    }
  }, [pendingEntry, setPendingEntry]);

  const [isFormOpen, setIsFormOpen] = useState(false);

  // Načítanie dát pri prihlásení
  const handleLogin = async () => {
    if (!pin.trim()) {
      alert('Zadajte PIN');
      return;
    }
    try {
      const response = await fetch(`/php/work_tracking_api.php?action=load&pin=${encodeURIComponent(pin)}`);
      const data = await response.json();
      
      if (data.success) {
        setIsLoggedIn(true);
        
        // Doplň total iba pre záznamy, kde chýba (nová migrácia), neprepisuj existujúce hodnoty
        const loadedEntries = (data.entries || []).map(entry => ({
          ...entry,
          total: entry.total != null ? entry.total : calculateEntryTotal(entry)
        }));
        
        setWorkEntries(loadedEntries);
        
        // Extrahuj unikátne firmy - zoskup podľa normalizovaného názvu
        const companyMap = new Map();
        loadedEntries.forEach(entry => {
          const normalized = normalizeText(entry.companyName);
          if (!companyMap.has(normalized)) {
            companyMap.set(normalized, entry.companyName);
          }
        });
        const uniqueCompanies = Array.from(companyMap.values()).sort();
        setCompanies(uniqueCompanies);
      } else {
        // Nový PIN - vytvor prázdnu evidenciu
        setIsLoggedIn(true);
        setWorkEntries([]);
        setCompanies([]);
      }
    } catch (error) {
      console.error('Chyba pri prihlásení:', error);
      alert('Chyba pri prihlásení');
    }
  };

  // Uloženie dát
  const saveData = async (entries) => {
    try {
      const response = await fetch(`/php/work_tracking_api.php?action=save&pin=${encodeURIComponent(pin)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries })
      });
      const data = await response.json();
      
      if (!data.success) {
        alert('Chyba pri ukladaní: ' + (data.message || 'Neznáma chyba'));
      }
    } catch (error) {
      console.error('Chyba pri ukladaní:', error);
      alert('Chyba pri ukladaní dát');
    }
  };

  // Pridanie nového záznamu
  const handleAddEntry = () => {
    if (!newEntry.companyName || !newEntry.description) {
      alert('Vyplňte názov firmy a popis práce');
      return;
    }
    if (!newEntry.workPeriodFrom) {
      alert('Vyplňte obdobie, ktorého sa práca týka');
      return;
    }
    if (!newEntry.note || !newEntry.note.trim()) {
      alert('Vyplňte poznámku — uveďte konkrétny detail (napr. meno pracovníka)');
      return;
    }

    // Kapitalizuj názov firmy
    const capitalizedCompanyName = capitalizeCompanyName(newEntry.companyName);

    const entry = {
      id: Date.now(),
      ...newEntry,
      companyName: capitalizedCompanyName,
      total: calculateEntryTotal(newEntry),
      invoiced: false, // Nový záznam nie je vyfakturovaný
      createdAt: new Date().toISOString()
    };

    const updatedEntries = [...workEntries, entry];
    setWorkEntries(updatedEntries);
    saveData(updatedEntries);

    // Aktualizuj zoznam firiem - pridaj len ak neexistuje (bez ohľadu na diakritiku a veľkosť)
    const normalizedNewCompany = normalizeText(capitalizedCompanyName);
    const companyExists = companies.some(c => normalizeText(c) === normalizedNewCompany);
    if (!companyExists) {
      setCompanies([...companies, capitalizedCompanyName].sort());
    }

    // Reset formulára
    setNewEntry({
      companyName: '',
      date: new Date().toISOString().split('T')[0],
      workPeriodFrom: new Date().toISOString().slice(0, 7),
      workPeriodTo: '',
      description: '',
      minutes: 0,
      unitPrice: 0,
      actualInvoicePrice: 0,
      note: ''
    });
  };

  // Odstránenie záznamu
  const handleDeleteEntry = (id) => {
    if (!window.confirm('Naozaj chcete odstrániť tento záznam?')) return;
    
    const updatedEntries = workEntries.filter(e => e.id !== id);
    setWorkEntries(updatedEntries);
    saveData(updatedEntries);
  };

  // Začať inline editovanie
  const handleStartEdit = (entry) => {
    setEditingId(entry.id);
    setEditedEntry({ ...entry });
  };

  // Zrušiť inline editovanie
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedEntry(null);
  };

  // Uložiť inline editovaný záznam
  const handleSaveEdit = () => {
    if (!editedEntry.companyName || !editedEntry.description) {
      alert('Vyplňte názov firmy a popis práce');
      return;
    }

    // Kapitalizuj názov firmy
    const capitalizedCompanyName = capitalizeCompanyName(editedEntry.companyName);

    const updatedEntries = workEntries.map(entry => 
      entry.id === editingId 
        ? { 
            ...editedEntry,
            companyName: capitalizedCompanyName,
            total: calculateEntryTotal(editedEntry),
            modifiedAt: new Date().toISOString()
          }
        : entry
    );

    setWorkEntries(updatedEntries);
    saveData(updatedEntries);
    
    // Aktualizuj zoznam firiem - pridaj len ak neexistuje (bez ohľadu na diakritiku a veľkosť)
    const normalizedEditedCompany = normalizeText(capitalizedCompanyName);
    const companyExists = companies.some(c => normalizeText(c) === normalizedEditedCompany);
    if (!companyExists) {
      setCompanies([...companies, capitalizedCompanyName].sort());
    }

    setEditingId(null);
    setEditedEntry(null);
  };

  // Aktualizovať editované pole
  const handleEditFieldChange = (field, value) => {
    setEditedEntry({
      ...editedEntry,
      [field]: value
    });
  };

  // Filtrovanie záznamov
  // Filtrovanie záznamov je teraz v utils/evidenciaPraceUtils.js
  // Ak je potrebné, môžeš pridať normalizeText do utils a použiť ho tam

  // Export do CSV
  const handleExportCSV = () => {
    const filtered = getFilteredEntries();
    if (filtered.length === 0) {
      alert('Žiadne záznamy na export');
      return;
    }

    const csvContent = [
      ['Dátum', 'Firma', 'Popis', 'Trvanie', 'Jednotková cena', 'Celkom', 'Poznámka'],
      ...filtered.map(e => [
        e.date,
        e.companyName,
        e.description,
        e.minutes != null ? e.minutes + ' min' : e.quantity + ' ks',
        e.unitPrice.toFixed(2),
        e.total.toFixed(2),
        e.note
      ])
    ].map(row => row.join(';')).join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `evidencia_prace_${filterMonth || 'vsetko'}_${selectedCompany || 'vsetky'}.csv`;
    link.click();
  };

  // Vytvorenie podkladu pre fakturáciu
  const handleCreateInvoice = () => {
    if (selectedForInvoice.length === 0) {
      alert('Označte aspoň jednu položku na fakturáciu');
      return;
    }

    // Získať len označené položky
    const selectedEntries = workEntries.filter(entry => selectedForInvoice.includes(entry.id));
    const total = calculateTotal(selectedEntries);
    
    const invoiceData = {
      company: selectedCompany || 'Všetky firmy',
      period: filterMonth,
      entries: selectedEntries,
      total: total
    };

    // Uložíme do localStorage pre použitie v inej komponente
    localStorage.setItem('invoiceData', JSON.stringify(invoiceData));
    
    // Označíme vybrané úlohy ako vyfakturované
    const updatedEntries = workEntries.map(entry => {
      if (selectedForInvoice.includes(entry.id)) {
        return { ...entry, invoiced: true, invoicedAt: new Date().toISOString() };
      }
      return entry;
    });
    
    setWorkEntries(updatedEntries);
    saveData(updatedEntries);
    
    // Zrušíme výber
    setSelectedForInvoice([]);
    
    // Otvoríme podklad pre fakturáciu
    window.open('/invoice-preview', '_blank');
  };

  // --- Hromadná úprava ---
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkFields, setBulkFields] = useState({
    companyName: { enabled: false, value: '' },
    unitPrice: { enabled: false, value: '' },
    workPeriodFrom: { enabled: false, value: '' },
    description: { enabled: false, value: '' },
    invoiced: { enabled: false, value: '' },
  });

  const toggleBulkField = (field) => {
    setBulkFields(prev => ({ ...prev, [field]: { ...prev[field], enabled: !prev[field].enabled } }));
  };

  const setBulkFieldValue = (field, value) => {
    setBulkFields(prev => ({ ...prev, [field]: { ...prev[field], value } }));
  };

  const handleApplyBulkEdit = () => {
    const activeFields = Object.entries(bulkFields).filter(([, f]) => f.enabled);
    if (activeFields.length === 0) { alert('Vyberte aspoň jedno pole na úpravu.'); return; }
    if (selectedForInvoice.length === 0) { alert('Označte aspoň jeden riadok.'); return; }
    if (!window.confirm(`Upraviť ${selectedForInvoice.length} záznam(ov)?`)) return;
    const updated = workEntries.map(entry => {
      if (!selectedForInvoice.includes(entry.id)) return entry;
      let changes = {};
      activeFields.forEach(([field, f]) => {
        if (field === 'unitPrice') {
          const v = parseFloat(f.value);
          if (!isNaN(v)) changes.unitPrice = v;
        } else if (field === 'invoiced') {
          changes.invoiced = f.value === 'true';
        } else {
          if (f.value.trim()) changes[field] = f.value.trim();
        }
      });
      const patched = { ...entry, ...changes };
      return { ...patched, total: calculateEntryTotal(patched) };
    });
    setWorkEntries(updated);
    saveData(updated);
    setSelectedForInvoice([]);
    setBulkEditMode(false);
    setBulkFields({ companyName: { enabled: false, value: '' }, unitPrice: { enabled: false, value: '' }, workPeriodFrom: { enabled: false, value: '' }, description: { enabled: false, value: '' }, invoiced: { enabled: false, value: '' } });
  };

  // Toggle checkbox pre výber položky na fakturáciu
  const handleToggleInvoiceSelection = (entryId) => {
    setSelectedForInvoice(prev => {
      if (prev.includes(entryId)) {
        return prev.filter(id => id !== entryId);
      } else {
        return [...prev, entryId];
      }
    });
  };

  // Označiť všetky filtrované položky
  const handleSelectAllForInvoice = () => {
    const filtered = getFilteredEntries(workEntries, selectedCompany, filterMonth, showInvoiced);
    const allIds = filtered.map(e => e.id);
    setSelectedForInvoice(allIds);
  };

  // Zrušiť výber všetkých položiek
  const handleDeselectAllForInvoice = () => {
    setSelectedForInvoice([]);
  };

  if (!isLoggedIn) {
    return (
      <div className="evidence-container">
        <div className="login-box">
          <h2>evidencia práce</h2>
          <p>Zadajte PIN pre prístup k vašej evidencii:</p>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="PIN"
            className="pin-input"
            autoFocus
            style={{ color: '#000' }}
          />
          <button onClick={handleLogin} className="btn-login">Prihlásiť</button>
        </div>
      </div>
    );
  }

  const filteredEntries = getFilteredEntries(workEntries, selectedCompany, filterMonth, showInvoiced);
  const totalSum = calculateTotal(filteredEntries);



  return (
    <div className="evidence-container">
      <div className="evidence-header">
        <h1>evidencia práce</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Link 
            to="/kalkulacka" 
            style={{ 
              padding: '10px 20px', 
              background: '#007bff', 
              color: 'white', 
              textDecoration: 'none', 
              borderRadius: '4px',
              fontSize: '14px',
              display: 'inline-block'
            }}
          >
            📊 kalkulačka
          </Link>
          <button onClick={() => setIsLoggedIn(false)} className="btn-logout">Odhlásiť</button>
        </div>
      </div>

      {/* Formulár pre nový záznam - collapsible */}
      <div style={{ background: 'white', borderRadius: 8, marginBottom: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div
          onClick={() => setIsFormOpen(o => !o)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', cursor: 'pointer', userSelect: 'none', background: isFormOpen ? '#f8fafc' : 'white' }}
        >
          <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>+ Nový záznam</span>
          <span style={{ fontSize: 18, color: '#94a3b8' }}>{isFormOpen ? '▲' : '▼'}</span>
        </div>
        {isFormOpen && (
          <div style={{ borderTop: '1px solid #e2e8f0' }}>
            <EvidenciaPraceForm
              newEntry={newEntry}
              setNewEntry={setNewEntry}
              handleAddEntry={handleAddEntry}
              companies={companies}
            />
          </div>
        )}
      </div>

      {/* Filter a zoznam záznamov */}
      <div className="entries-section">
        <h2 style={{ margin: '0 0 8px 0' }}>Záznamy prác</h2>


        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} style={{ fontSize: 13, border: '1px solid #ddd', borderRadius: 5, padding: '4px 8px' }}>
            <option value="">Všetky firmy</option>
            {companies.map(company => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
          <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} style={{ fontSize: 13, border: '1px solid #ddd', borderRadius: 5, padding: '4px 8px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            {[['not_invoiced', 'Nevyfakturované'], ['invoiced', 'Vyfakturované'], ['all', 'Všetko']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setShowInvoiced(val)}
                style={{ padding: '3px 10px', borderRadius: 5, border: '1px solid #ccc', cursor: 'pointer', fontWeight: showInvoiced === val ? 700 : 400, background: showInvoiced === val ? '#1d4ed8' : '#f1f5f9', color: showInvoiced === val ? '#fff' : '#334155', fontSize: 12 }}
              >{label}</button>
            ))}
          </div>
          <button onClick={handleExportCSV} className="btn-export" style={{ fontSize: 13, padding: '4px 12px' }}>Export CSV</button>
          <button onClick={handleSelectAllForInvoice} className="btn-select" style={{ fontSize: 13, padding: '4px 10px' }}>✓ Označiť všetky</button>
          <button onClick={handleDeselectAllForInvoice} className="btn-select" style={{ fontSize: 13, padding: '4px 10px' }}>✕ Zrušiť výber</button>
          <span style={{ fontSize: 12, color: '#666' }}>({selectedForInvoice.length} označených)</span>
          <button onClick={handleCreateInvoice} className="btn-invoice" style={{ fontSize: 13, padding: '4px 12px' }}>Podklad pre fakturáciu ({selectedForInvoice.length})</button>
          <button
            onClick={() => { setBulkEditMode(m => !m); }}
            style={{ fontSize: 13, padding: '4px 12px', background: bulkEditMode ? '#0ea5e9' : '#f1f5f9', color: bulkEditMode ? 'white' : '#1e293b', border: '1px solid #cbd5e1', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
          >✏️ Hromadná úprava{bulkEditMode ? ' ▲' : ' ▼'}</button>
        </div>

        {/* Panel hromadnej úpravy */}
        {bulkEditMode && (
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 16px', marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0369a1', marginBottom: 10 }}>✏️ Hromadná úprava — zaškrtni polia, ktoré chceš zmeniť pre <strong>{selectedForInvoice.length}</strong> označených záznam(ov)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              {/* Firma */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, color: '#334155' }}>
                <span><input type="checkbox" checked={bulkFields.companyName.enabled} onChange={() => toggleBulkField('companyName')} style={{ marginRight: 4 }} />Firma</span>
                <input
                  type="text"
                  list="companies-list-bulk"
                  disabled={!bulkFields.companyName.enabled}
                  value={bulkFields.companyName.value}
                  onChange={e => setBulkFieldValue('companyName', e.target.value)}
                  placeholder="Nový názov firmy"
                  style={{ fontSize: 13, padding: '3px 7px', border: '1px solid #cbd5e1', borderRadius: 5, minWidth: 160, opacity: bulkFields.companyName.enabled ? 1 : 0.4 }}
                />
                <datalist id="companies-list-bulk">{companies.map(c => <option key={c} value={c} />)}</datalist>
              </label>
              {/* Cena/jedn. */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, color: '#334155' }}>
                <span><input type="checkbox" checked={bulkFields.unitPrice.enabled} onChange={() => toggleBulkField('unitPrice')} style={{ marginRight: 4 }} />Cena/jedn. (€)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  disabled={!bulkFields.unitPrice.enabled}
                  value={bulkFields.unitPrice.value}
                  onChange={e => setBulkFieldValue('unitPrice', e.target.value)}
                  placeholder="napr. 50"
                  style={{ fontSize: 13, padding: '3px 7px', border: '1px solid #cbd5e1', borderRadius: 5, width: 90, opacity: bulkFields.unitPrice.enabled ? 1 : 0.4 }}
                />
              </label>
              {/* Obdobie */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, color: '#334155' }}>
                <span><input type="checkbox" checked={bulkFields.workPeriodFrom.enabled} onChange={() => toggleBulkField('workPeriodFrom')} style={{ marginRight: 4 }} />Obdobie (rok-mesiac)</span>
                <input
                  type="month"
                  disabled={!bulkFields.workPeriodFrom.enabled}
                  value={bulkFields.workPeriodFrom.value}
                  onChange={e => setBulkFieldValue('workPeriodFrom', e.target.value)}
                  style={{ fontSize: 13, padding: '3px 7px', border: '1px solid #cbd5e1', borderRadius: 5, opacity: bulkFields.workPeriodFrom.enabled ? 1 : 0.4 }}
                />
              </label>
              {/* Popis */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, color: '#334155' }}>
                <span><input type="checkbox" checked={bulkFields.description.enabled} onChange={() => toggleBulkField('description')} style={{ marginRight: 4 }} />Popis</span>
                <input
                  type="text"
                  disabled={!bulkFields.description.enabled}
                  value={bulkFields.description.value}
                  onChange={e => setBulkFieldValue('description', e.target.value)}
                  placeholder="Nový popis"
                  style={{ fontSize: 13, padding: '3px 7px', border: '1px solid #cbd5e1', borderRadius: 5, minWidth: 160, opacity: bulkFields.description.enabled ? 1 : 0.4 }}
                />
              </label>
              {/* Status fakturácie */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, color: '#334155' }}>
                <span><input type="checkbox" checked={bulkFields.invoiced.enabled} onChange={() => toggleBulkField('invoiced')} style={{ marginRight: 4 }} />Status fakturácie</span>
                <select
                  disabled={!bulkFields.invoiced.enabled}
                  value={bulkFields.invoiced.value}
                  onChange={e => setBulkFieldValue('invoiced', e.target.value)}
                  style={{ fontSize: 13, padding: '3px 7px', border: '1px solid #cbd5e1', borderRadius: 5, opacity: bulkFields.invoiced.enabled ? 1 : 0.4 }}
                >
                  <option value="">-- vyber --</option>
                  <option value="false">Nevyfakturované</option>
                  <option value="true">✓ Vyfakturované</option>
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                onClick={handleApplyBulkEdit}
                style={{ background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 6, padding: '6px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >Aplikovať zmeny</button>
              <button
                onClick={() => { setBulkEditMode(false); setSelectedForInvoice([]); }}
                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer', color: '#64748b' }}
              >Zrušiť</button>
            </div>
          </div>
        )}

        <div className="entries-table">
          <table>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input 
                    type="checkbox" 
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleSelectAllForInvoice();
                      } else {
                        handleDeselectAllForInvoice();
                      }
                    }}
                    checked={filteredEntries.length > 0 && selectedForInvoice.length === filteredEntries.length}
                  />
                </th>
                <th>Dátum</th>
                <th>Obdobie</th>
                <th>Firma</th>
                <th>Popis</th>
                <th>Počet položiek</th>
                <th>Cena/jedn.</th>
                <th>Celkom</th>
                <th>Poznámka</th>
                <th>Status</th>
                <th>Akcie</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan="11" className="no-data">Žiadne záznamy</td>
                </tr>
              ) : (
                filteredEntries.map(entry => (
                  <tr key={entry.id} className={editingId === entry.id ? 'editing-row' : ''}>
                    {editingId === entry.id ? (
                      // Editovací režim - inline editing
                      <>
                        <td>
                          <input 
                            type="checkbox" 
                            checked={selectedForInvoice.includes(entry.id)}
                            onChange={() => handleToggleInvoiceSelection(entry.id)}
                            disabled={entry.invoiced}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            value={editedEntry.date}
                            onChange={(e) => handleEditFieldChange('date', e.target.value)}
                            className="inline-edit-input"
                          />
                        </td>
                        <td>
                          <input
                            type="month"
                            value={editedEntry.workPeriodFrom || ''}
                            onChange={(e) => handleEditFieldChange('workPeriodFrom', e.target.value)}
                            className="inline-edit-input"
                            style={{ borderColor: editedEntry.workPeriodFrom ? undefined : '#fca5a5' }}
                          />
                          <input
                            type="month"
                            value={editedEntry.workPeriodTo || ''}
                            onChange={(e) => handleEditFieldChange('workPeriodTo', e.target.value)}
                            className="inline-edit-input"
                            min={editedEntry.workPeriodFrom || undefined}
                            placeholder="do (nepovinné)"
                            style={{ marginTop: 4 }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            list="companies-list-edit"
                            value={editedEntry.companyName}
                            onChange={(e) => handleEditFieldChange('companyName', e.target.value)}
                            className="inline-edit-input"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editedEntry.description}
                            onChange={(e) => handleEditFieldChange('description', e.target.value)}
                            className="inline-edit-input"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            value={editedEntry.minutes != null ? editedEntry.minutes : ''}
                            onChange={(e) => handleEditFieldChange('minutes', parseInt(e.target.value) || 0)}
                            className="inline-edit-input inline-edit-number"
                            placeholder="min"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            value={editedEntry.unitPrice}
                            onChange={(e) => handleEditFieldChange('unitPrice', parseFloat(e.target.value) || 0)}
                            className="inline-edit-input inline-edit-number"
                          />
                        </td>
                        <td><strong>{calculateEntryTotal(editedEntry).toFixed(2)} €</strong></td>
                        <td>
                          <input
                            type="text"
                            value={editedEntry.note}
                            onChange={(e) => handleEditFieldChange('note', e.target.value)}
                            className="inline-edit-input"
                          />
                        </td>
                        <td>
                          <span style={{ 
                            padding: '3px 8px', 
                            borderRadius: '3px', 
                            fontSize: '12px',
                            backgroundColor: editedEntry.invoiced ? '#d4edda' : '#fff3cd',
                            color: editedEntry.invoiced ? '#155724' : '#856404'
                          }}>
                            {editedEntry.invoiced ? '✓ Vyfakturované' : 'Nevyfakturované'}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            onClick={handleSaveEdit}
                            className="btn-save"
                            title="Uložiť"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="btn-cancel-inline"
                            title="Zrušiť"
                          >
                            ✕
                          </button>
                        </td>
                      </>
                    ) : (
                      // Zobrazovací režim
                      <>
                        <td>
                          <input 
                            type="checkbox" 
                            checked={selectedForInvoice.includes(entry.id)}
                            onChange={() => handleToggleInvoiceSelection(entry.id)}
                            disabled={entry.invoiced}
                            title={entry.invoiced ? 'Už vyfakturované' : 'Označiť na fakturáciu'}
                          />
                        </td>
                        <td>{entry.date}</td>
                        <td>
                          {entry.workPeriodFrom
                            ? entry.workPeriodTo && entry.workPeriodTo !== entry.workPeriodFrom
                              ? `${entry.workPeriodFrom} – ${entry.workPeriodTo}`
                              : entry.workPeriodFrom
                            : entry.workPeriod || '—'}
                        </td>
                        <td>{entry.companyName}</td>
                        <td>{entry.description}</td>
                        <td>
                          {entry.minutes != null
                            ? `${entry.minutes} min`
                            : `${typeof entry.quantity === 'number' ? entry.quantity.toFixed(2) : entry.quantity} ks`}
                        </td>
                        <td>{entry.unitPrice.toFixed(2)} €</td>
                        <td><strong>{entry.total.toFixed(2)} €</strong></td>
                        <td>{entry.note}</td>
                        <td>
                          <span style={{ 
                            padding: '3px 8px', 
                            borderRadius: '3px', 
                            fontSize: '12px',
                            backgroundColor: entry.invoiced ? '#d4edda' : '#fff3cd',
                            color: entry.invoiced ? '#155724' : '#856404'
                          }}>
                            {entry.invoiced ? '✓ Vyfakturované' : 'Nevyfakturované'}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => handleStartEdit(entry)}
                            className="btn-edit"
                            title="Upraviť"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="btn-delete"
                            title="Odstrániť"
                          >
                            🗑️
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
            {filteredEntries.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="7" className="total-label" style={{textAlign: 'right', fontWeight: 'bold'}}>Spolu:</td>
                  <td className="total-value" style={{textAlign: 'right', fontWeight: 'bold', color: '#1976d2'}}>
                    {totalSum.toFixed(2)} €
                  </td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default EvidenciaPrace;
